<template>
  <div>
    <b-modal
      id="modal-minstock"
      v-model="modalMinStock"
      cancel-variant="outline-secondary"
      hide-footer
      centered
      title="Minimal Stock"
    >
      <b-form @submit.prevent="submitMinStock">
        <div>
          <div class="messages">
            <b-row>
              <b-col
                cols="11"
                md="9"
              >
                <input
                  v-model="itemMinStock"
                  type="number"
                  min="1"
                  class="form-control"
                  placeholder="Put Minimal Stock"
                >
              </b-col>
              <b-col
                cols="11"
                md="3"
              >
                <b-button
                  variant="primary"
                  class="pull-right w-100"
                  type="submit"
                >
                  Submit
                </b-button>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-form>
    </b-modal>

    <item-add-new
      :is-add-new-item-sidebar-active.sync="isAddNewItemSidebarActive"
      :role-options="roleOptions"
      @refetch-data="refetchData"
    />

    <item-edit
      :is-edit-item-sidebar-active.sync="isEditItemSidebarActive"
      :item-id="itemId"
      :item-name="itemName"
      :item-code="itemCode"
      :item-category="itemCategory"
      :item-age-category="itemAgeCategory"
      :item-est-age="itemEstAge"
      :item-est-delivery="itemEstDelivery"
      :item-allotment="itemAllotment"
      :chosen-unit="chosenUnit"
    />

    <app-page-shell
      title="Items"
      subtitle="Manage item catalog, stock thresholds, import templates, and export reports."
    >
      <div class="m-2">
        <b-row>
          <b-col cols="5">
            <label>Tanggal Awal</label>
            <b-form-datepicker
              id="start-datepicker"
              v-model="startDateFilter"
              class="mb-2"
            />
          </b-col>
          <b-col cols="5">
            <label>Tanggal Akhir</label>
            <b-form-datepicker
              id="end-datepicker"
              v-model="endDateFilter"
              class="mb-2"
            />
          </b-col>
          <b-col
            cols="2"
            class="d-flex align-items-center justify-content-center"
          >
            <b-button
              variant="primary"
              :disabled="isLoadingExport"
              @click="exportData"
            >
              <feather-icon icon="DownloadIcon" /> Export
              <b-spinner
                v-if="isLoadingExport"
                small
                type="grow"
              />
            </b-button>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>

            <div
              class="view-switch ml-2"
              role="group"
              aria-label="Switch view"
            >
              <button
                type="button"
                class="view-switch__btn"
                :class="{ 'view-switch__btn--active': viewMode === 'card' }"
                @click="viewMode = 'card'"
              >
                <feather-icon
                  icon="GridIcon"
                  size="14"
                />
                <span>Cards</span>
              </button>
              <button
                type="button"
                class="view-switch__btn"
                :class="{ 'view-switch__btn--active': viewMode === 'table' }"
                @click="viewMode = 'table'"
              >
                <feather-icon
                  icon="ListIcon"
                  size="14"
                />
                <span>Table</span>
              </button>
            </div>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
              <input
                id="fileUpload"
                ref="file"
                name="fileUpload"
                type="file"
                accept=".xlsx, .xls, .csv"
                hidden
                @change="uploadFile"
              >
              <b-button
                v-if="userRole === 'admin' || userRole === 'super-admin'"
                variant="success"
                class="d-inline-block mr-1"
                @click="chooseFile"
              >
                <span class="text-nowrap">Import</span>
              </b-button>

              <b-button
                v-if="userRole === 'admin' || userRole === 'super-admin'"
                variant="outline-secondary"
                class="d-inline-block mr-1"
                @click="downloadTemplate"
              >
                <span class="text-nowrap">Download Template</span>
              </b-button>

              <b-button
                v-if="userRole === 'admin' || userRole === 'super-admin'"
                variant="primary"
                @click="isAddNewItemSidebarActive = true"
              >
                <span class="text-nowrap">Add Item</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        v-if="viewMode === 'table'"
        ref="refItemListTable"
        class="position-relative"
        :items="fetchItems"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #table-busy>
          <shimmer-table-rows
            :rows="Math.min(perPage, 8)"
            :columns="tableColumns.length - 1"
          />
        </template>

        <template #cell(item_name)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.item_name }}</span>
          </div>
        </template>

        <template #cell(item_category)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.item_category }}</span>
          </div>
        </template>

        <template #cell(item_code)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.item_code }}</span>
          </div>
        </template>

        <template #cell(item_unit)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">{{ data.item.unit != null ? data.item.unit.unit_name : "-" }}</span>
          </div>
        </template>

        <template #cell(item_min_stock)="data">
          <div class="text-nowrap">
            <span class="align-text-top text-capitalize">
              {{ data.item.item_min_stock !== 0 ? data.item.item_min_stock : "Need to be updated" }}
            </span>
          </div>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item @click="showStockHistory(data.item)">
              <feather-icon icon="RefreshCcwIcon" />
              <span class="align-middle ml-50">Stock History</span>
            </b-dropdown-item>

            <b-dropdown-item @click="setMinStock(data.item.item_id)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Set Min Stock</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="userRole === 'admin' || userRole === 'super-admin'"
              @click="editItem(data.item)"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="userRole === 'admin' || userRole === 'super-admin'"
              @click="deleteItem(data.item.item_id)"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <!-- Card view -->
      <div
        v-if="viewMode === 'card'"
        class="item-cards"
      >
        <div
          v-if="isLoadingItems"
          class="item-cards__loading"
        >
          <shimmer-card-grid :count="perPage" />
        </div>

        <div
          v-else-if="!items.length"
          class="item-cards__empty"
        >
          <feather-icon
            icon="PackageIcon"
            size="30"
          />
          <strong>No matching records found</strong>
        </div>

        <div
          v-else
          class="item-cards__body"
        >
          <div class="item-cards__rail d-none d-md-flex">
            <button
              v-for="letter in alphabet"
              :key="letter"
              type="button"
              class="item-cards__rail-letter"
              :class="{ 'item-cards__rail-letter--active': availableLetters.has(letter) }"
              :disabled="!availableLetters.has(letter)"
              @click="scrollToLetter(letter)"
            >
              {{ letter }}
            </button>
          </div>

          <div
            ref="cardScrollArea"
            class="item-cards__scroll"
          >
            <div
              v-for="group in groupedItems"
              :id="`item-letter-${group.letter}`"
              :key="group.letter"
              class="item-cards__group"
            >
              <div class="item-cards__group-heading">
                <span>{{ group.letter }}</span>
                <em>{{ group.items.length }} {{ group.items.length === 1 ? 'item' : 'items' }}</em>
              </div>

              <div class="item-cards__grid">
                <div
                  v-for="item in group.items"
                  :key="item.item_id"
                  class="item-card"
                >
                  <div class="item-card__top">
                    <span class="item-card__icon">
                      <feather-icon
                        icon="PackageIcon"
                        size="20"
                      />
                    </span>
                    <b-dropdown
                      variant="link"
                      no-caret
                      right
                      class="item-card__menu"
                    >
                      <template #button-content>
                        <feather-icon
                          icon="MoreVerticalIcon"
                          size="16"
                          class="align-middle text-body"
                        />
                      </template>
                      <b-dropdown-item @click="showStockHistory(item)">
                        <feather-icon icon="RefreshCcwIcon" />
                        <span class="align-middle ml-50">Stock History</span>
                      </b-dropdown-item>
                      <b-dropdown-item @click="setMinStock(item.item_id)">
                        <feather-icon icon="EditIcon" />
                        <span class="align-middle ml-50">Set Min Stock</span>
                      </b-dropdown-item>
                      <b-dropdown-item
                        v-if="userRole === 'admin' || userRole === 'super-admin'"
                        @click="editItem(item)"
                      >
                        <feather-icon icon="EditIcon" />
                        <span class="align-middle ml-50">Edit</span>
                      </b-dropdown-item>
                      <b-dropdown-item
                        v-if="userRole === 'admin' || userRole === 'super-admin'"
                        @click="deleteItem(item.item_id)"
                      >
                        <feather-icon icon="TrashIcon" />
                        <span class="align-middle ml-50">Delete</span>
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>

                  <h6 class="item-card__name">
                    {{ item.item_name || '-' }}
                  </h6>

                  <b-badge
                    pill
                    variant="light-primary"
                    class="item-card__category"
                  >
                    {{ item.item_category || 'Uncategorized' }}
                  </b-badge>

                  <ul class="item-card__meta">
                    <li>
                      <feather-icon
                        icon="HashIcon"
                        size="13"
                      />
                      <span>{{ item.item_code || '-' }}</span>
                    </li>
                    <li>
                      <feather-icon
                        icon="BoxIcon"
                        size="13"
                      />
                      <span>{{ item.unit ? item.unit.unit_name : '-' }}</span>
                    </li>
                    <li :class="{ 'item-card__meta--warning': item.item_min_stock === 0 }">
                      <feather-icon
                        icon="AlertTriangleIcon"
                        size="13"
                      />
                      <span>{{ item.item_min_stock !== 0 ? `Min stock: ${item.item_min_stock}` : 'Min stock not set' }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries
            </span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="dataMeta.of || (currentPage * perPage)"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </app-page-shell>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BFormDatepicker,
  BModal,
  BForm,
  BSpinner,
} from 'bootstrap-vue'
import axios from 'axios'
import vSelect from 'vue-select'
import store from '@/store'
import moment from 'moment-timezone'
import { saveAs } from 'file-saver'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ShimmerCardGrid from '@core/components/skeleton/ShimmerCardGrid.vue'
import ShimmerTableRows from '@core/components/skeleton/ShimmerTableRows.vue'
import { ref, onUnmounted } from '@vue/composition-api'
import { avatarText } from '@core/utils/filter'
import apiEndpoints from '@/constants/apiEndpoints'
import listPagePersistence from '@/mixins/listPagePersistence'
import AppPageShell from '@/views/apps/components/AppPageShell.vue'
import useItemList from './ItemList' // Adjusted import to match renamed JS function
import itemModule from '../itemModule'
import ItemAddNew from './ItemAddNew.vue'
import ItemEdit from './ItemEdit.vue'

export default {
  components: {
    ItemEdit,
    ItemAddNew,
    ShimmerCardGrid,
    ShimmerTableRows,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormDatepicker,
    BModal,
    BForm,
    BSpinner,
    vSelect,
    AppPageShell,
  },
  mixins: [listPagePersistence(['currentPage'])],
  data() {
    return {
      isLoadingExport: false,
      isItemManagement: false,
      userRole: '',
      file: null,
      // Item Data (Changed nulls to empty strings and 0)
      itemId: 0,
      itemName: '',
      itemCode: '',
      itemCategory: '',
      itemAgeCategory: '',
      itemEstAge: '',
      itemEstDelivery: '',
      itemAllotment: '',
      chosenUnit: '',

      // Min Stock
      itemMinStock: '',
      modalMinStock: false,

      alphabet: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
    }
  },
  computed: {
    groupedItems() {
      const groups = {}
      this.items.forEach(item => {
        const initial = (item.item_name || '').trim().charAt(0).toUpperCase()
        const letter = /[A-Z]/.test(initial) ? initial : '#'
        if (!groups[letter]) groups[letter] = []
        groups[letter].push(item)
      })
      return Object.keys(groups)
        .sort()
        .map(letter => ({ letter, items: groups[letter] }))
    },
    availableLetters() {
      return new Set(this.groupedItems.map(group => group.letter))
    },
  },
  setup() {
    const APP_STORE_MODULE_NAME = 'app-item'

    // Register module
    if (!store.hasModule(APP_STORE_MODULE_NAME)) {
      store.registerModule(APP_STORE_MODULE_NAME, itemModule)
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(APP_STORE_MODULE_NAME)) {
        store.unregisterModule(APP_STORE_MODULE_NAME)
      }
    })

    const isAddNewItemSidebarActive = ref(false)
    const isEditItemSidebarActive = ref(false)

    const statusOptions = [
      { label: 'Pending', value: 'pending' },
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ]

    const roleOptions = [
      { label: 'Admin', value: 'admin' },
      { label: 'Author', value: 'author' },
      { label: 'Editor', value: 'editor' },
      { label: 'Maintainer', value: 'maintainer' },
      { label: 'Subscriber', value: 'subscriber' },
    ]

    const {
      fetchItems,
      fetchItemsData,
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refItemListTable,
      refetchData,

      // Switchable views
      viewMode,
      items,
      isLoadingItems,

      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserStatusVariant,

      roleFilter,
      statusFilter,
      startDateFilter,
      endDateFilter,
    } = useItemList()

    return {
      // Sidebar
      isAddNewItemSidebarActive,
      isEditItemSidebarActive,

      fetchItems,
      fetchItemsData,
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,

      // Switchable views
      viewMode,
      items,
      isLoadingItems,
      refItemListTable,
      refetchData,

      avatarText,

      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserStatusVariant,

      roleOptions,
      statusOptions,

      roleFilter,
      statusFilter,
      startDateFilter,
      endDateFilter,
    }
  },
  mounted() {
    this.userRole = this.$cookies.get('UserRole')
    const userData = this.$cookies.get('userData')

    if (userData && userData.role && userData.role.role_id === 1) {
      this.isItemManagement = true
    }

    if (this.viewMode === 'card') {
      this.fetchItemsData()
    }

    this.$root.$on('refreshTable', () => {
      this.refetchData()
    })
  },
  methods: {
    scrollToLetter(letter) {
      const el = document.getElementById(`item-letter-${letter}`)
      const container = this.$refs.cardScrollArea
      if (!el) return
      if (container) {
        const top = el.offsetTop - container.offsetTop
        container.scrollTo({ top, behavior: 'smooth' })
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    deleteItem(id) {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_DELETE_ITEMS}/${id}`
      axios
        .get(url, { headers })
        .then(response => {
          if (response.data.success === true) {
            this.refetchData()
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Congratulation',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'Your data has been deleted!',
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Notification',
                icon: 'BellIcon',
                variant: 'danger',
                text: response.data.message,
              },
            })
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    chooseFile() {
      document.getElementById('fileUpload').click()
    },
    exportData() {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_EXPORT_ITEMS}`
      this.isLoadingExport = true

      axios.get(url, {
        headers,
        responseType: 'arraybuffer',
        params: {
          date_start: this.startDateFilter,
          date_end: this.endDateFilter,
        },
      }).then(response => {
        let filename = 'report_item'
        if (this.startDateFilter) filename += `_${this.startDateFilter}`
        if (this.endDateFilter) filename += `_${this.endDateFilter}`
        filename += '.xlsx'

        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        saveAs(blob, filename)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Export Success',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      }).finally(() => {
        this.isLoadingExport = false
      })
    },
    downloadTemplate() {
      const userToken = this.$cookies.get('userToken')
      const url = `${process.env.VUE_APP_API_URL}items/import-template`
      axios.get(url, {
        headers: { Authorization: `Bearer ${userToken}` },
        responseType: 'blob',
      }).then(response => {
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        saveAs(blob, 'template_import_barang.xlsx')
      })
    },
    uploadFile() {
      const firstFile = this.$refs.file.files[0]
      if (!firstFile) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Import Failed, No file selected.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      this.file = firstFile
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${userToken}`,
      }

      const formData = new FormData()
      formData.append('file', firstFile, firstFile.name)
      formData.append('time', moment().format())

      const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_IMPORT_ITEMS}`

      axios.post(url, formData, { headers }).then(response => {
        if (response.data.success === true) {
          this.refetchData()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Import Success',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Import Failed. Check server logs.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      })
    },
    editItem(data) {
      this.itemId = data.item_id
      this.itemName = data.item_name
      this.itemCode = data.item_code
      this.itemCategory = data.item_category

      this.itemAgeCategory = data.item_age_category && data.item_age_category !== 'undefined'
        ? {
            label: this.capitalizedText(data.item_age_category),
            value: data.item_age_category,
          }
        : null

      this.itemEstAge = data.item_est_age && data.item_est_age !== 'undefined' ? data.item_est_age : ''
      this.itemEstDelivery = data.item_est_delivery && data.item_est_delivery !== 'undefined' ? data.item_est_delivery : ''

      this.itemAllotment = data.item_allotment && data.item_allotment !== 'undefined'
        ? {
            label: this.capitalizedText(data.item_allotment),
            value: data.item_allotment,
          }
        : null

      this.chosenUnit = data.unit
        ? { label: data.unit.unit_name, value: data.unit.unit_id }
        : null

      this.isEditItemSidebarActive = true
    },
    showStockHistory(data) {
      localStorage.setItem('ItemDetails', JSON.stringify(data))
      this.$router.push({ name: 'apps-items-history-lists' })
    },
    setMinStock(id) {
      this.itemId = id
      const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_MIN_STOCK_ITEM}${id}`
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      axios.get(url, { headers }).then(response => {
        if (response.data.success === true) {
          this.itemMinStock = response.data.data.item_min_stock_number
        } else {
          this.itemMinStock = 0
        }
        this.modalMinStock = true
      })
    },
    submitMinStock() {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      const url = `${process.env.VUE_APP_API_URL}${apiEndpoints.API_UPDATE_MIN_STOCK_ITEM}${this.itemId}`
      const formData = {
        item_min_stock_number: this.itemMinStock,
        time: moment().format(),
      }

      axios
        .post(url, formData, { headers, crossDomain: true })
        .then(response => {
          if (response.data.success === true) {
            this.modalMinStock = false
            this.refetchData()
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Update Success',
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Something went wrong',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          }
        })
    },
    numbersFormat(value) {
      return parseFloat(value).toFixed(2)
    },
    capitalizedText(text) {
      if (!text) return ''
      return text.charAt(0).toUpperCase() + text.slice(1)
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

// --- View switcher ---------------------------------------------------
.view-switch {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem;
  border: 1px solid #ebe9f1;
  border-radius: 999px;
  background: #f8f8fc;
}

.view-switch__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.32rem 0.75rem;
  color: #6e6b7b;
  border: 0;
  border-radius: 999px;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(&--active) {
    color: #2b8a7e;
  }

  &--active {
    color: #fff;
    background: linear-gradient(135deg, #2b8a7e 0%, #255f85 100%);
    box-shadow: 0 4px 10px rgba(43, 138, 126, 0.28);
  }
}

// --- Card view ---------------------------------------------------------
.item-cards {
  position: relative;
  min-height: 220px;
  padding: 0 1.25rem 0.5rem;
}

.item-cards__loading {
  padding-top: 0.25rem;
}

.item-cards__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 3.5rem 1rem;
  color: #b9b9c3;
}

.item-cards__body {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.item-cards__scroll {
  flex: 1 1 auto;
  min-width: 0;
  max-height: 640px;
  overflow-y: auto;
  padding-right: 0.35rem;
  scroll-behavior: smooth;
}

.item-cards__group {
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0.25rem;
  }
}

.item-cards__group-heading {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  margin-bottom: 0.75rem;
  border-radius: 8px;
  background: rgba(247, 248, 251, 0.94);
  backdrop-filter: blur(3px);

  span {
    color: #2b8a7e;
    font-size: 1rem;
    font-weight: 800;
    letter-spacing: 0.02em;
  }

  em {
    color: #b9b9c3;
    font-size: 0.72rem;
    font-style: normal;
  }
}

.item-cards__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 1rem;
}

.item-card {
  position: relative;
  padding: 1rem 1rem 0.9rem;
  border: 1px solid #eeeef4;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(24, 30, 48, 0.04);
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(43, 138, 126, 0.25);
    box-shadow: 0 14px 28px rgba(24, 30, 48, 0.1);
  }
}

.item-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.65rem;
}

.item-card__icon {
  display: grid;
  width: 46px;
  height: 46px;
  place-items: center;
  color: #2b8a7e;
  border-radius: 50%;
  background: rgba(43, 138, 126, 0.12);
}

.item-card__menu {
  margin-top: -0.35rem;
  margin-right: -0.5rem;
}

.item-card__name {
  margin: 0 0 0.45rem;
  overflow: hidden;
  color: #1f2a44;
  font-size: 0.98rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-card__category {
  margin-bottom: 0.75rem;
  font-size: 0.7rem;
}

.item-card__meta {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0;
  margin: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    overflow: hidden;
    color: #6e6b7b;
    font-size: 0.78rem;
    text-overflow: ellipsis;
    white-space: nowrap;

    svg {
      flex: 0 0 auto;
      color: #b9b9c3;
    }

    &.item-card__meta--warning {
      color: #e8890c;

      svg {
        color: #e8890c;
      }
    }
  }
}

// --- A-Z rail ------------------------------------------------------------
.item-cards__rail {
  position: sticky;
  top: 0.5rem;
  flex: 0 0 30px;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 0.5rem 0.2rem;
  border-radius: 999px;
  background: #f8f8fc;
}

.item-cards__rail-letter {
  width: 20px;
  padding: 0.06rem 0;
  color: #c8c7d1;
  border: 0;
  background: transparent;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1.35;
  transition: color 0.12s ease, transform 0.12s ease;

  &:disabled {
    cursor: default;
  }

  &--active {
    color: #255f85;
    cursor: pointer;

    &:hover {
      color: #2b8a7e;
      transform: scale(1.25);
    }
  }
}

@media (max-width: 767.98px) {
  .item-cards {
    padding: 0 0.75rem 0.5rem;
  }

  .item-cards__grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 0.75rem;
  }

  .view-switch__btn span {
    display: none;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
